/**
 * Australian financial year (1 July – 30 June) and BAS quarter helpers.
 *
 * Dates are calendar dates as `YYYY-MM-DD` strings, not instants, so results do
 * not depend on the runtime's time zone.
 */

export interface DateRange {
  /** Inclusive start date, `YYYY-MM-DD`. */
  start: string
  /** Inclusive end date, `YYYY-MM-DD`. */
  end: string
}

export interface FinancialYear extends DateRange {
  /** ATO-style label, e.g. `2026-27`. */
  label: string
}

export interface BasQuarter extends DateRange {
  /** e.g. `Q2 2026-27`. */
  label: string
  quarter: 1 | 2 | 3 | 4
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

function parseCalendarDate(input: string): { year: number; month: number } {
  const match = ISO_DATE.exec(input)
  if (!match) throw new RangeError(`Expected a YYYY-MM-DD date, got "${input}"`)
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
    throw new RangeError(`Not a valid calendar date: "${input}"`)
  }
  return { year, month }
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

function isoDate(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
}

/** Year in which the financial year containing (year, month) starts. */
function startYearOf(year: number, month: number): number {
  return month >= 7 ? year : year - 1
}

function labelFor(startYear: number): string {
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}`
}

export function financialYearOf(date: string): FinancialYear {
  const { year, month } = parseCalendarDate(date)
  const startYear = startYearOf(year, month)
  return {
    label: labelFor(startYear),
    start: isoDate(startYear, 7, 1),
    end: isoDate(startYear + 1, 6, 30),
  }
}

/** The income year named by a `YYYY-YY` label such as `2026-27`, or null when it is not one (the second part must be the next year). */
export function financialYearFromLabel(label: string): FinancialYear | null {
  if (!/^\d{4}-\d{2}$/.test(label)) return null
  const startYear = Number(label.slice(0, 4))
  if (labelFor(startYear) !== label) return null
  return { label, start: isoDate(startYear, 7, 1), end: isoDate(startYear + 1, 6, 30) }
}

export function basQuarterOf(date: string): BasQuarter {
  const { year, month } = parseCalendarDate(date)
  const startYear = startYearOf(year, month)
  // Months since 1 July: Jul=0 … Jun=11
  const offset = (month + 5) % 12
  const quarter = (Math.floor(offset / 3) + 1) as BasQuarter["quarter"]

  const firstMonthOffset = (quarter - 1) * 3
  const startMonth = ((6 + firstMonthOffset) % 12) + 1
  const startCalendarYear = startMonth >= 7 ? startYear : startYear + 1
  const endMonth = startMonth + 2

  return {
    label: `Q${quarter} ${labelFor(startYear)}`,
    quarter,
    start: isoDate(startCalendarYear, startMonth, 1),
    end: isoDate(startCalendarYear, endMonth, daysInMonth(startCalendarYear, endMonth)),
  }
}
